import { useQuery } from '@tanstack/react-query';
import { useQueryClient } from '@tanstack/react-query';
import { useIsAuthenthicated } from '../auth';
import { apiPaths } from '../queryClient';
import { useSignalREffect } from '../signalr/SignalRContext';
import {
  ActiveClassroom,
  ActiveSimulation,
  ClassroomUpdatedNotification,
  SimulationCompletedNotification,
  SimulationEventMessage,
  SimulationUpdatedNotification,
} from '../types';

const simulationQueryKey = [{ path: apiPaths.activeSimulations }];

export const useActiveSimulation = () => {
  const queryClient = useQueryClient();
  const isAuthenticated = useIsAuthenthicated();

  const { data: activeSimulation, isLoading: isLoadingActiveSimulation } = useQuery<ActiveSimulation>({
    enabled: isAuthenticated,
    queryKey: simulationQueryKey,
    select: data => data || null, // Convert empty body to null
    // SignalR is the primary channel for keeping this in sync (see setActiveSimulation calls in
    // useSignalRActiveSimulation). The poll and focus refetch below are just a fallback safety net
    // in case an event is missed (e.g. dropped connection), so a stale session doesn't stick
    // around indefinitely.
    refetchInterval: data => (data ? 300000 : false),
    refetchOnWindowFocus: true,
  });

  return {
    activeSimulation,
    isLoadingActiveSimulation,
    setActiveSimulation(activeSimulation: ActiveSimulation | null) {
      queryClient.setQueryData(simulationQueryKey, activeSimulation);
    },
  };
};

const classroomQueryKey = [{ path: apiPaths.activeClassrooms }];

export const useActiveClassroom = () => {
  const queryClient = useQueryClient();
  const isAuthenticated = useIsAuthenthicated();

  const { data: activeClassroom, isLoading: isLoadingActiveClassroom } = useQuery<ActiveClassroom>({
    enabled: isAuthenticated,
    queryKey: classroomQueryKey,
    select: data => data || null, // Convert empty body to null
  });

  return {
    activeClassroom,
    isLoadingActiveClassroom,
    setActiveClassroom(activeClassroom: ActiveClassroom | null) {
      queryClient.setQueryData(classroomQueryKey, activeClassroom);
    },
  };
};

export const useSignalRActiveClassroom = () => {
  const { setActiveClassroom } = useActiveClassroom();

  useSignalREffect(
    SimulationEventMessage.ClassroomUpdated,
    (...args) => {
      const notification = args[0] as ClassroomUpdatedNotification;
      console.debug('classroomUpdated', notification);

      setActiveClassroom(notification);
    },
    [setActiveClassroom],
  );
};

export const useSignalRActiveSimulation = () => {
  const { setActiveSimulation } = useActiveSimulation();

  useSignalREffect(
    SimulationEventMessage.SimulationUpdated,
    (...args) => {
      const notification = args[0] as SimulationUpdatedNotification;
      console.debug('simulationUpdated', notification);

      setActiveSimulation(notification);
    },
    [setActiveSimulation],
  );

  useSignalREffect(
    SimulationEventMessage.SimulationCompleted,
    (...args) => {
      const notification = args[0] as SimulationCompletedNotification;
      console.debug('simulationCompleted', notification);

      setActiveSimulation(null);
    },
    [setActiveSimulation],
  );
};
